import React, { useMemo } from 'react';
import {
  Alert,
  Card,
  Empty,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ExperimentOutlined,
  InfoCircleOutlined,
  StarFilled,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { EChartsOption } from 'echarts';
import EChart from '../charts/EChart';
import type {
  ComparisonBranch,
  ComparisonResult,
  GetSaveComparisonResponse,
  ScenarioTemplate,
} from '../../types/saves';

const { Text, Paragraph } = Typography;

const USER_COLOR = '#1677ff';
const REFERENCE_COLOR = '#fa8c16';
const BRANCH_COLORS = ['#52c41a', '#faad14', '#722ed1', '#f5222d', '#13c2c2', '#eb2f96'];
const EMPTY_METRICS: ComparisonBranch['metrics'] = {};
const EMPTY_BRANCHES: ComparisonBranch[] = [];

const numericValue = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  return null;
};

const displayValue = (v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isFinite(v) ? formatNumber(v) : String(v);
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
};

const formatNumber = (n: number) => {
  if (Math.abs(n) >= 1000) return n.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  if (Math.abs(n) >= 1) return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 4 });
};

const collectMetricNames = (
  comparison: ComparisonResult,
  template?: ScenarioTemplate | null
): string[] => {
  const fromTemplate = Array.isArray((template?.comparison as { metrics?: string[] } | undefined)?.metrics)
    ? ((template!.comparison as { metrics?: string[] }).metrics as string[])
    : [];
  if (fromTemplate.length > 0) return fromTemplate;

  const set = new Set<string>();
  comparison.user_branch?.metrics &&
    Object.keys(comparison.user_branch.metrics).forEach((k) => set.add(k));
  comparison.reference_branch?.metrics &&
    Object.keys(comparison.reference_branch.metrics).forEach((k) => set.add(k));
  comparison.branches?.forEach((b) =>
    b.metrics ? Object.keys(b.metrics).forEach((k) => set.add(k)) : null
  );
  return Array.from(set);
};

interface ChartProps {
  comparison: ComparisonResult;
  template?: ScenarioTemplate | null;
  metricNames: string[];
}

const ComparisonChart: React.FC<ChartProps> = ({ comparison, template, metricNames }) => {
  const userLabel = comparison.user_branch?.label || 'user';
  const userMetrics = comparison.user_branch?.metrics ?? EMPTY_METRICS;
  const referenceBranch = comparison.reference_branch || null;
  const branches = comparison.branches ?? EMPTY_BRANCHES;

  const seriesBranches: { branch: ComparisonBranch; color: string; isUser: boolean }[] = useMemo(
    () => [
      { branch: { label: userLabel, metrics: userMetrics, source: 'user' }, color: USER_COLOR, isUser: true },
      ...(referenceBranch ? [{ branch: referenceBranch, color: REFERENCE_COLOR, isUser: false }] : []),
      ...branches.map((b, idx) => ({
        branch: b,
        color: BRANCH_COLORS[idx % BRANCH_COLORS.length],
        isUser: false,
      })),
    ],
    [branches, referenceBranch, userLabel, userMetrics]
  );

  const option: EChartsOption = useMemo(() => {
    const goalsByMetric = new Map<string, { target: unknown; operator?: string }>();
    template?.goals?.forEach((g) => {
      if (g.metric) goalsByMetric.set(g.metric, { target: g.target, operator: g.operator });
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      legend: {
        data: seriesBranches.map((s) => s.branch.label),
        bottom: 0,
      },
      grid: { left: 40, right: 16, top: 24, bottom: 56, containLabel: true },
      xAxis: {
        type: 'category',
        data: metricNames,
        axisLabel: { interval: 0, rotate: metricNames.length > 4 ? 25 : 0 },
      },
      yAxis: { type: 'value' },
      series: seriesBranches.map((s) => ({
        name: s.branch.label,
        type: 'bar',
        data: metricNames.map((metric) => {
          const v = numericValue(s.branch.metrics?.[metric]);
          return v == null ? { value: 0, itemStyle: { color: '#d9d9d9' } } : v;
        }),
        itemStyle: {
          color: s.color,
          borderColor: s.isUser ? '#000' : 'transparent',
          borderWidth: s.isUser ? 2 : 0,
        },
        emphasis: {
          itemStyle: {
            borderColor: '#000',
            borderWidth: 2,
          },
        },
        label: {
          show: true,
          position: 'top',
          formatter: (params) => {
            const branch = seriesBranches[params.seriesIndex as number]?.branch;
            const raw = branch?.metrics?.[params.name];
            return displayValue(raw);
          },
        },
        markLine: s.isUser
          ? {
              symbol: 'none',
              data: metricNames
                .map((metric, idx) => {
                  const goal = goalsByMetric.get(metric);
                  const target = goal ? numericValue(goal.target) : null;
                  return target == null ? null : { xAxis: idx, yAxis: target };
                })
                .filter((d): d is { xAxis: number; yAxis: number } => d != null),
              lineStyle: { color: '#fa8c16', type: 'dashed' },
              label: { formatter: 'goal' },
            }
          : undefined,
      })),
    };
  }, [metricNames, seriesBranches, template]);

  if (metricNames.length === 0) {
    return (
      <Empty
        description="В comparison spec не описаны метрики для сравнения"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return <EChart option={option} height={Math.max(320, 80 + metricNames.length * 60)} />;
};

const MetricsTable: React.FC<{
  comparison: ComparisonResult;
  metricNames: string[];
}> = ({ comparison, metricNames }) => {
  const userLabel = comparison.user_branch?.label || 'user';
  const userMetrics = comparison.user_branch?.metrics ?? EMPTY_METRICS;
  const referenceBranch = comparison.reference_branch || null;
  const branches = comparison.branches ?? EMPTY_BRANCHES;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
        }}
      >
        <thead>
          <tr>
            <th style={th}>Метрика</th>
            <th style={{ ...th, color: USER_COLOR }}>
              <StarFilled /> {userLabel}
            </th>
            {referenceBranch ? (
              <th style={{ ...th, color: REFERENCE_COLOR }}>{referenceBranch.label}</th>
            ) : null}
            {branches.map((b, idx) => (
              <th key={b.label + idx} style={{ ...th, color: BRANCH_COLORS[idx % BRANCH_COLORS.length] }}>
                {b.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metricNames.map((metric) => (
            <tr key={metric}>
              <td style={tdName}>{metric}</td>
              <td style={tdUser}>{displayValue(userMetrics[metric])}</td>
              {referenceBranch ? (
                <td style={td}>{displayValue(referenceBranch.metrics?.[metric])}</td>
              ) : null}
              {branches.map((b, idx) => (
                <td key={b.label + idx} style={td}>
                  {displayValue(b.metrics?.[metric])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  borderBottom: '1px solid #f0f0f0',
  fontWeight: 600,
  background: '#fafafa',
};

const td: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid #f0f0f0',
};

const tdName: React.CSSProperties = {
  ...td,
  fontWeight: 500,
};

const tdUser: React.CSSProperties = {
  ...td,
  background: '#e6f4ff',
  fontWeight: 600,
};

interface ComparisonViewProps {
  data: GetSaveComparisonResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

export const ComparisonView: React.FC<ComparisonViewProps> = ({ data, isLoading, isError }) => {
  if (isLoading) {
    return (
      <Card title={<Space><ExperimentOutlined /> Сравнение веток</Space>} size="small" style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card title={<Space><ExperimentOutlined /> Сравнение веток</Space>} size="small" style={{ marginBottom: 16 }}>
        <Alert type="error" showIcon message="Не удалось загрузить comparison" />
      </Card>
    );
  }

  if (data.supported === false) {
    if (data.reason === 'not_a_scenario') {
      return null;
    }
    return (
      <Card title={<Space><ExperimentOutlined /> Сравнение веток</Space>} size="small" style={{ marginBottom: 16 }}>
        <Empty
          description={
            data.reason === 'no_comparison_spec'
              ? 'Сценарий не содержит comparison-веток (типичная ситуация для mechanical-сценариев)'
              : data.reason === 'no_reference'
                ? 'Для scenario comparison не задан reference run в шаблоне'
              : `Comparison недоступен: ${data.reason}`
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  const { comparison, scenarioTemplate, source, status } = data;
  if (!comparison) {
    return (
      <Card title={<Space><ExperimentOutlined /> Сравнение веток</Space>} size="small" style={{ marginBottom: 16 }}>
        <Empty description="Comparison ещё не вычислен" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }

  if (comparison.comparison_status === 'pending') {
    return (
      <Card title={<Space><ExperimentOutlined /> Сравнение веток</Space>} size="small" style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          icon={<ClockCircleOutlined />}
          message="Сравнение слишком тяжёлое для синхронного расчёта"
          description={
            <Space direction="vertical" size={4}>
              <Text>
                Будет вычислено асинхронно. Загляните позже — результат закэшируется по{' '}
                <Text code>comparison_cache_key</Text>.
              </Text>
              {comparison.estimate ? (
                <Text type="secondary">
                  Оценка: {comparison.estimate.branch_count} веток × {comparison.estimate.ticks} тиков ≈{' '}
                  {comparison.estimate.estimated_cpu_ms} мс CPU
                </Text>
              ) : null}
              {comparison.reason ? <Text type="secondary">Причина: {comparison.reason}</Text> : null}
            </Space>
          }
        />
      </Card>
    );
  }

  const metricNames = collectMetricNames(comparison, scenarioTemplate);
  const isPreview = source === 'preview';
  const isUserMetricsEmpty =
    !comparison.user_branch?.metrics ||
    metricNames.every((m) => numericValue(comparison.user_branch?.metrics?.[m]) == null);

  return (
    <Card
      title={
        <Space>
          <ExperimentOutlined />
          Сравнение веток
          <Tag color={status === 'completed' ? 'green' : 'blue'}>
            {status === 'completed' ? 'Сценарий завершён' : 'В процессе'}
          </Tag>
          {isPreview ? (
            <Tooltip title="Это превью: считается inline по текущим событиям, без завершения сценария">
              <Tag>preview</Tag>
            </Tooltip>
          ) : (
            <Tag color="default">stored</Tag>
          )}
          {comparison.cached ? (
            <Tooltip title="Результат взят из comparison_cache">
              <Tag color="purple">cached</Tag>
            </Tooltip>
          ) : null}
        </Space>
      }
      size="small"
      style={{ marginBottom: 16 }}
    >
      {isPreview && isUserMetricsEmpty ? (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 12 }}
          message="Метрики ветки пользователя ещё не посчитаны"
          description="Завершите сценарий или дождитесь, пока движок посчитает финальные метрики. Альтернативные ветки доступны как ориентир."
        />
      ) : null}

      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Голубым выделена ваша ветка. Пунктирные линии на графике — целевые значения метрик из сценария.
      </Paragraph>

      <Tabs
        defaultActiveKey="chart"
        items={[
          {
            key: 'chart',
            label: 'График',
            children: (
              <ComparisonChart
                comparison={comparison}
                template={scenarioTemplate}
                metricNames={metricNames}
              />
            ),
          },
          {
            key: 'table',
            label: 'Таблица',
            children: <MetricsTable comparison={comparison} metricNames={metricNames} />,
          },
        ]}
      />
    </Card>
  );
};

export default ComparisonView;
